import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchOverview, fetchTotais, Overview, TotalGrupo } from '../../../api/bancoSync/bancoSyncApi';
import { notify } from '../../../shared/feedback/notify';
import ModalSync from '../../../components/sync-painel/ModalSync';
import TabelaSistema, { ColunaTabela } from '../../../components/sync-painel/TabelaSistema';
import { formatarDataHoraCurta } from '../../../components/sync-painel/statusBadge';
import '../../../shared/feedback/aviso-de-tela.css';

const COMPETENCIA_REGEX = /^(0[1-9]|1[0-2])\/\d{4}$/;

const competenciaDe = (d: Date): string => `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

/** Default: as 3 últimas competências, terminando no mês atual. */
const periodoDefault = (): { inicio: string; fim: string } => {
  const fim = new Date();
  const inicio = new Date(fim.getFullYear(), fim.getMonth() - 2, 1);
  return { inicio: competenciaDe(inicio), fim: competenciaDe(fim) };
};

const moeda = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const COR_TIPO: Record<TotalGrupo['tipo'], string> = {
  ENTRADA: '#16a34a',
  SAIDA: '#dc2626',
  RESULTADO: '#1A1C1B',
};

interface PeriodoFiltro {
  inicio: string;
  fim: string;
}

/** Filtros do período em modal, no padrão do projeto: aplicação em bloco, recebe o aplicado. */
const FiltroPeriodoModal: React.FC<{
  aplicado: PeriodoFiltro;
  onAplicar: (p: PeriodoFiltro) => void;
  onClose: () => void;
}> = ({ aplicado, onAplicar, onClose }) => {
  const [inicio, setInicio] = useState(aplicado.inicio);
  const [fim, setFim] = useState(aplicado.fim);

  return (
    <ModalSync
      titulo="Período dos totais"
      subtitulo="Competências no formato MM/AAAA, inclusivas (máximo 36 meses)"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="filtro-btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="filtro-btn-primary"
            onClick={() => {
              if (!COMPETENCIA_REGEX.test(inicio) || !COMPETENCIA_REGEX.test(fim)) {
                notify.warning('Informe as duas competências no formato MM/AAAA (ex: 06/2026).');
                return;
              }
              onAplicar({ inicio, fim });
            }}
          >
            Aplicar Filtros
          </button>
        </>
      }
    >
      <div className="filtro-section">
        <h3 className="filtro-section-title">Competências</h3>
        <div className="filtro-grid-2">
          <div className="filtro-field">
            <label htmlFor="totais-inicio">Inicial</label>
            <input
              id="totais-inicio"
              className="premium-text-input"
              type="text"
              value={inicio}
              placeholder="MM/AAAA"
              onChange={(e) => setInicio(e.target.value)}
            />
          </div>
          <div className="filtro-field">
            <label htmlFor="totais-fim">Final</label>
            <input
              id="totais-fim"
              className="premium-text-input"
              type="text"
              value={fim}
              placeholder="MM/AAAA"
              onChange={(e) => setFim(e.target.value)}
            />
          </div>
        </div>
      </div>
    </ModalSync>
  );
};

/** Um dos lados (tabelas ou views) renderizado na tabela padrão do sistema. */
const CardDeTotais: React.FC<{
  titulo: string;
  hint: React.ReactNode;
  lado: 'tabelas' | 'views';
  periodo: PeriodoFiltro;
}> = ({ titulo, hint, lado, periodo }) => {
  const { data, error, isError, isLoading } = useQuery({
    queryKey: ['banco-sync', 'totais', lado, periodo],
    queryFn: () => fetchTotais(lado, periodo),
  });

  useEffect(() => {
    if (isError) {
      notify.errorFrom(error, `Não foi possível carregar os totais (${lado}). Confira o período; se persistir, acione a TI.`, {
        toastId: `banco-sync-totais-${lado}`,
      });
    }
  }, [isError, error, lado]);

  const colunas = useMemo<ColunaTabela<TotalGrupo>[]>(() => {
    if (!data) return [];
    return [
      { titulo: 'Grupo', largura: 300, texto: (g) => g.rotulo },
      ...data.competencias.map(
        (c): ColunaTabela<TotalGrupo> => ({
          titulo: c,
          largura: 130,
          texto: (g) => moeda(g.porCompetencia[c] ?? 0),
          render: (g) => <span style={{ color: COR_TIPO[g.tipo] }}>{moeda(g.porCompetencia[c] ?? 0)}</span>,
        }),
      ),
      {
        titulo: 'Total do período',
        largura: 150,
        texto: (g) => moeda(g.total),
        render: (g) => <strong style={{ color: COR_TIPO[g.tipo] }}>{moeda(g.total)}</strong>,
      },
    ];
  }, [data]);

  return (
    <div style={{ marginBottom: 24 }}>
      <div className="admin-card-title" style={{ marginBottom: 4 }}>
        {titulo}
      </div>
      <p className="admin-hint" style={{ marginTop: 0, marginBottom: 12 }}>
        {hint}
      </p>

      {isError && (
        <div className="erro-de-tela" role="alert">
          Não foi possível carregar os totais deste lado. Os números abaixo, se houver, podem estar incompletos.
        </div>
      )}

      <div
        style={{
          width: '100%',
          paddingTop: 30,
          background: 'var(--tema-superficie, white)',
          borderRadius: 16,
          border: '1px var(--tema-linha-2, #CACACA) solid',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
        }}
      >
        <TabelaSistema
          colunas={colunas.length ? colunas : [{ titulo: 'Grupo', largura: 300, texto: () => '' }]}
          itens={data?.grupos ?? []}
          chave={(g) => g.rotulo}
          carregando={isLoading}
          vazio={isError ? 'Os totais não puderam ser carregados — veja o aviso acima.' : 'Nenhum valor no período informado.'}
        />
      </div>
    </div>
  );
};

/**
 * Aba "Totais por competência": os mesmos números em dois lados deliberadamente
 * diferentes — direto das tabelas (bruto, tudo que existe) e das views (o que o Contac
 * lê, com as regras contábeis embutidas). Divergência entre os lados é INFORMAÇÃO
 * (regra de negócio agindo), não defeito — a hint de cada card explica.
 */
const TotaisSection: React.FC = () => {
  const [periodo, setPeriodo] = useState<PeriodoFiltro>(periodoDefault);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);

  // Mesmo cache do overview da outra aba — aqui só para datar o refresh das views.
  const { data: overview } = useQuery<Overview>({ queryKey: ['banco-sync', 'overview'], queryFn: fetchOverview });
  const ultimoRefresh = overview?.viewsRefresh?.[0];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
        <p className="admin-hint" style={{ margin: 0 }}>
          Período aplicado: <strong>{periodo.inicio}</strong> a <strong>{periodo.fim}</strong>
        </p>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setFiltrosAbertos(true)}>
          Filtros
        </button>
      </div>

      <div className="aviso-de-tela" style={{ margin: '10px 0 20px' }}>
        Os dois lados <strong>não batem por definição</strong>: as tabelas mostram tudo que existe no banco
        (bruto); as views aplicam as regras do Contac (boleto pago com rateio de taxas, situação do lançamento,
        exclusões de classificação, corte a partir de 01/2025). Divergência entre eles é a regra agindo — use os
        dois lados para entender de onde vem cada número.
      </div>

      <CardDeTotais
        titulo="Direto das tabelas (bruto)"
        hint="Tudo que está gravado, sem filtro contábil. Boletos em três linhas (emitidos, pagos e pagos por mês de referência), cada uma seguida da quebra por base; lançamentos Nasajon por sinal, em bruto × situação 1."
        lado="tabelas"
        periodo={periodo}
      />

      <CardDeTotais
        titulo="Das views (o que o Contac lê)"
        hint={
          <>
            Agregado de <strong>mv_resultados_por_placa_calculados</strong> — os números dos dashboards.
            {ultimoRefresh
              ? ` Última atualização das views: ${formatarDataHoraCurta(ultimoRefresh.refreshed_at)}.`
              : ' Sem registro de atualização das views.'}
            {' '}Número estranho aqui com tabela certa = view desatualizada — atualize na aba Sincronização.
          </>
        }
        lado="views"
        periodo={periodo}
      />

      {filtrosAbertos && (
        <FiltroPeriodoModal
          aplicado={periodo}
          onClose={() => setFiltrosAbertos(false)}
          onAplicar={(p) => {
            setPeriodo(p);
            setFiltrosAbertos(false);
          }}
        />
      )}
    </>
  );
};

export default TotaisSection;
